// Pure helpers for the retention-due issue's decide flow (Document
// Compliance Program — destruction).
//
// The `retention-due` detector asks one question with three real
// answers: destroy it, keep it longer, or put it on legal hold. Those
// are not Cleanup dispositions (they don't route through
// /api/cleanup/decide) — they are compliance actions on the document
// itself, so the rail renders a dedicated panel and this module holds
// everything about it that can be tested without a browser.
//
// Root-safe (imports nothing) so it's unit-tested from tests/web/ —
// same convention as expiration-issue.ts / pm-entity-issue.ts.

// Minimal structural subset of CleanupIssueSummary this module reads.
export interface RetentionIssueLike {
  detectorSlug: string;
  affectedRecords: ReadonlyArray<{ kind: string; id: string; label?: string }>;
}

export function isRetentionDueIssue(issue: RetentionIssueLike): boolean {
  return issue.detectorSlug === "retention-due";
}

/** The document this issue is about. The detector attaches exactly one
 *  affected record and it is the document — but read it defensively
 *  rather than assuming index 0, because a panel that destroys the
 *  WRONG document is the worst bug this feature could have. */
export function retentionDocumentId(issue: RetentionIssueLike): string | null {
  const records = issue.affectedRecords.filter((r) => r.kind === "activity");
  return records.length === 1 ? records[0]!.id : null;
}

/** Must be typed exactly. Mirrors DESTROY_CONFIRM_PHRASE in
 *  workers/api/src/routes/files.ts — the server checks it too, so a
 *  drift here costs a clear error message, never a wrong deletion. */
export const DESTROY_CONFIRM_PHRASE = "DESTROY PERMANENTLY";

export function destroyConfirmMatches(typed: string): boolean {
  return typed.trim() === DESTROY_CONFIRM_PHRASE;
}

export interface DispositionReasonOption {
  code: string;
  label: string;
  /** "When to use this" — Cleanup UX rule 9. */
  when: string;
}

export const DISPOSITION_REASON_OPTIONS: DispositionReasonOption[] = [
  {
    code: "retention_schedule_reached",
    label: "Reached the end of its retention period",
    when: "The ordinary case: the schedule's years have run and nothing ties this record to an open matter.",
  },
  {
    code: "client_requested_removal",
    label: "The client asked us to remove it",
    when: "Use only when the retention period has ALSO run out — a client request never shortens a statutory minimum.",
  },
  {
    code: "compliance_correction",
    label: "Compliance correction",
    when: "The document should not have been stored here at all (wrong household, a duplicate upload, data we are not permitted to keep).",
  },
  {
    code: "other_with_text",
    label: "Something else (explain below)",
    when: "Anything the three above don't describe. Your written reason carries the whole explanation.",
  },
];

/** Minimum characters for a written reason. The server enforces the
 *  same floor; this is so the button can be honest about it up front
 *  rather than bouncing the person after they commit. */
export const REASON_MIN_LENGTH = 10;

export function reasonIsSufficient(reason: string): boolean {
  return reason.trim().length >= REASON_MIN_LENGTH;
}

/** Can the destroy button fire yet? One function so the button's
 *  disabled state and the "what's missing" hint can never disagree. */
export function destroyBlockedReason(input: {
  eligible: boolean | null;
  eligibilityMessage: string | null;
  reasonCode: string;
  reason: string;
  typedConfirm: string;
}): string | null {
  if (input.eligible === false) {
    return input.eligibilityMessage ?? "This document cannot be destroyed.";
  }
  if (input.eligible === null) return "Checking whether this can be destroyed…";
  if (!input.reasonCode) return "Pick a reason first.";
  if (!reasonIsSufficient(input.reason)) {
    return "Write a sentence explaining the decision.";
  }
  if (!destroyConfirmMatches(input.typedConfirm)) {
    return `Type ${DESTROY_CONFIRM_PHRASE} to confirm.`;
  }
  return null;
}

/** Plain-language before→after, stated as a sentence rather than a
 *  field diff (Cleanup UX rule 3). Deliberately blunt: this is the
 *  last thing a person reads before an action with no undo. */
export function destroyConsequence(filename: string | null): string {
  const name = filename ?? "This document";
  return (
    `${name} will be permanently deleted from SharePoint and from the firm's duplicate copy. ` +
    `There is no undo and no recycle bin — this is the one action in the OS that cannot be reversed. ` +
    `What remains afterward is the record OF the destruction: who did it, when, under which retention class, and why. ` +
    `If there is any chance this file relates to an open dispute, audit, or examination, place a legal hold instead.`
  );
}

/** Default review date for "keep it longer": one year out. A year is
 *  long enough not to become busywork and short enough that a document
 *  kept "just in case" gets looked at again while anyone still
 *  remembers why. */
export function defaultExtensionDate(todayIso: string): string {
  const d = new Date(`${todayIso}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return todayIso;
  d.setUTCFullYear(d.getUTCFullYear() + 1);
  return d.toISOString().slice(0, 10);
}
